import { useState, useEffect } from 'react'
import { Users, FileText, ShoppingBag, TrendingUp } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { usePermissions } from '@/hooks/usePermissions'
import { dashboardService } from '@/services/dashboardService'
import { apiClient } from '@/services/apiClient'
import type { DashboardOverview } from '@/services/dashboardService'

export default function Dashboard() {
  const navigate = useNavigate()
  const { canViewMetrics } = usePermissions()
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState<DashboardOverview | null>(null)
  const [registrationEnabled, setRegistrationEnabled] = useState(true)
  const [togglingRegistration, setTogglingRegistration] = useState(false)

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const data = await dashboardService.getOverview()
      setOverview(data)
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSettings = async () => {
    try {
      const { data } = await apiClient.get('/auth/settings')
      const reg = data.settings?.find((s: any) => s.key === 'registration_enabled')
      if (reg) setRegistrationEnabled(reg.value === 'true')
    } catch (error) {
      console.error('Erro ao carregar settings:', error)
    }
  }

  const toggleRegistration = async () => {
    setTogglingRegistration(true)
    try {
      const newValue = registrationEnabled ? 'false' : 'true'
      await apiClient.put('/auth/settings/registration_enabled', { value: newValue })
      setRegistrationEnabled(!registrationEnabled)
    } catch (error) {
      console.error('Erro ao atualizar setting:', error)
    } finally {
      setTogglingRegistration(false)
    }
  }

  useEffect(() => {
    loadDashboardData()
    loadSettings()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Visão geral do sistema</p>
        </div>
        {canViewMetrics() && (
          <button
            onClick={() => navigate('/metrics')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Ver Métricas Completas
          </button>
        )}
      </div>

      {/* Registration Toggle */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Cadastro de novos usuarios</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {registrationEnabled
              ? 'Novos usuarios podem se cadastrar no web app'
              : 'Cadastro desabilitado — botao "Cadastre-se" oculto no login'}
          </p>
        </div>
        <button
          onClick={toggleRegistration}
          disabled={togglingRegistration}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
            registrationEnabled ? 'bg-green-500' : 'bg-gray-300'
          } ${togglingRegistration ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
              registrationEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Usuários</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.stats.users || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Posts</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.stats.posts || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-50 rounded-lg">
              <ShoppingBag className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Produtos</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.stats.products || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-orange-50 rounded-lg">
              <TrendingUp className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Parceiros</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.stats.partners || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Metrics */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <Users className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Métricas de Usuários</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total</span>
              <span className="font-medium">{overview?.stats.users || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Novos (últimos 30 dias)</span>
              <span className="font-medium">{overview?.recentUsers || 0}</span>
            </div>
            <div className="pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Por Role:</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Users:</span>
                  <span>{overview?.usersByRole.user || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Creators:</span>
                  <span>{overview?.usersByRole.creator || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Admins:</span>
                  <span>{overview?.usersByRole.admin || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>God:</span>
                  <span>{overview?.usersByRole.god || 0}</span>
                </div>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Por Status:</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Ativos:
                  </span>
                  <span className="font-medium text-green-600">{overview?.usersByStatus.active || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                    Inativos:
                  </span>
                  <span className="font-medium text-gray-600">{overview?.usersByStatus.inactive || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Suspensos:
                  </span>
                  <span className="font-medium text-red-600">{overview?.usersByStatus.suspended || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Metrics */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <FileText className="h-5 w-5 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Métricas de Conteúdo</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total</span>
              <span className="font-medium">{overview?.stats.posts || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Novos (últimos 30 dias)</span>
              <span className="font-medium">{overview?.recentPosts || 0}</span>
            </div>
          </div>
        </div>

        {/* Marketplace Metrics */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <ShoppingBag className="h-5 w-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Métricas Marketplace</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Produtos</span>
              <span className="font-medium">{overview?.stats.products || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Parceiros</span>
              <span className="font-medium">{overview?.stats.partners || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Novos produtos (últimos 30 dias)</span>
              <span className="font-medium">{overview?.recentProducts || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}